import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "@/hooks/use-toast";
import { groupChatService, GroupMessageWithUser, GroupMessageReaction } from "@/services/groupChatService";

interface GroupTypingUser {
  user_id: string;
  username: string;
}

export const useGroupChat = (eventId: string | undefined) => {
  const { user } = useAuth();
  const [groupChatId, setGroupChatId] = useState<string | null>(null);
  const [messages, setMessages] = useState<GroupMessageWithUser[]>([]);
  const [reactions, setReactions] = useState<{ [messageId: string]: GroupMessageReaction[] }>({});
  const [typingUsers, setTypingUsers] = useState<GroupTypingUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'disconnected' | 'reconnecting'>('disconnected');
  const channelsRef = useRef<any[]>([]);

  useEffect(() => {
    if (user && eventId) {
      initializeGroupChat();
      setupRealtimeSubscriptions();
    }

    return () => {
      cleanup();
    };
  }, [user, eventId]);

  const cleanup = () => {
    channelsRef.current.forEach(channel => {
      supabase.removeChannel(channel);
    });
    channelsRef.current = [];
  };

  const initializeGroupChat = async () => {
    try {
      console.log("Initializing group chat for event:", eventId);
      
      const groupChat = await groupChatService.getEventGroupChat(eventId!);
      if (!groupChat) {
        console.error("No group chat found for event:", eventId);
        return;
      }

      setGroupChatId(groupChat.id);
      await loadMessages(groupChat.id);
      await loadReactions(groupChat.id);
      
    } catch (error) {
      console.error("Error initializing group chat:", error);
      toast({
        title: "Error",
        description: "Failed to load group chat",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (chatId: string) => {
    try {
      const messagesData = await groupChatService.getGroupMessages(chatId);
      setMessages(messagesData);
    } catch (error) {
      console.error("Error loading messages:", error);
    }
  };

  const loadReactions = async (chatId: string) => {
    try {
      const reactionsData = await groupChatService.getGroupMessageReactions(chatId);
      setReactions(reactionsData);
    } catch (error) {
      console.error("Error loading reactions:", error);
    }
  };

  const setupRealtimeSubscriptions = () => {
    if (!user || !eventId) return;

    setConnectionStatus('reconnecting');

    // Subscribe to new messages
    const messagesChannel = supabase
      .channel(`group-messages-${eventId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'group_messages',
        },
        async (payload) => {
          const newMessage = payload.new as any;
          // Fetch the complete message with user info
          try {
            const messageWithUser = await supabase
              .from("group_messages")
              .select(`
                *,
                user_profiles!fk_group_messages_user_profiles(
                  display_name,
                  first_name,
                  last_name
                )
              `)
              .eq("id", newMessage.id)
              .single();

            if (messageWithUser.data) {
              const formattedMessage = {
                ...messageWithUser.data,
                user: {
                  username: messageWithUser.data.user_profiles?.display_name || 
                           `${messageWithUser.data.user_profiles?.first_name || ''} ${messageWithUser.data.user_profiles?.last_name || ''}`.trim() || 
                           'Unknown User',
                  display_name: messageWithUser.data.user_profiles?.display_name || 'Unknown User'
                }
              };
              setMessages(prev => [...prev, formattedMessage]);
            }
          } catch (error) {
            console.error("Error fetching new message details:", error);
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'group_messages',
        },
        (payload) => {
          const updatedMessage = payload.new as any;
          setMessages(prev => prev.map(msg => 
            msg.id === updatedMessage.id ? { ...msg, ...updatedMessage } : msg
          ));
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          setConnectionStatus('connected');
        } else if (status === 'CHANNEL_ERROR') {
          setConnectionStatus('disconnected');
        }
      });

    // Subscribe to message reactions
    const reactionsChannel = supabase
      .channel(`group-reactions-${eventId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'group_message_reactions',
        },
        () => {
          // Reload reactions when they change
          if (groupChatId) {
            loadReactions(groupChatId);
          }
        }
      )
      .subscribe();

    // Subscribe to typing status
    const typingChannel = supabase
      .channel(`group-typing-${eventId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'group_typing_status',
        },
        async (payload) => {
          const typingStatus = payload.new as any;
          if (typingStatus && typingStatus.user_id !== user.id) {
            // Fetch user info for typing indicator
            try {
              const { data: userProfile } = await supabase
                .from("user_profiles")
                .select("display_name, first_name, last_name")
                .eq("user_id", typingStatus.user_id)
                .single();

              const username = userProfile?.display_name || 
                              `${userProfile?.first_name || ''} ${userProfile?.last_name || ''}`.trim() || 
                              'Unknown User';

              if (typingStatus.is_typing) {
                setTypingUsers(prev => {
                  const existing = prev.find(u => u.user_id === typingStatus.user_id);
                  if (!existing) {
                    return [...prev, { user_id: typingStatus.user_id, username }];
                  }
                  return prev;
                });
              } else {
                setTypingUsers(prev => prev.filter(u => u.user_id !== typingStatus.user_id));
              }
            } catch (error) {
              console.error("Error fetching typing user info:", error);
            }
          }
        }
      )
      .subscribe();

    channelsRef.current = [messagesChannel, reactionsChannel, typingChannel];
  };

  const sendMessage = async (content: string) => {
    if (!groupChatId || !content.trim() || sending) return;

    setSending(true);
    try {
      await groupChatService.sendGroupMessage(groupChatId, content);
    } catch (error) {
      console.error("Error sending group message:", error);
      toast({
        title: "Failed to Send",
        description: "Your message could not be sent. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSending(false);
    }
  };

  const handleReaction = async (messageId: string, emoji: string) => {
    if (!user) return;

    try {
      const existingReaction = reactions[messageId]?.find(
        r => r.user_id === user.id && r.emoji === emoji
      );

      if (existingReaction) {
        await groupChatService.removeGroupMessageReaction(messageId, emoji);
      } else {
        await groupChatService.addGroupMessageReaction(messageId, emoji);
      }
    } catch (error) {
      console.error("Error handling reaction:", error);
      toast({
        title: "Error",
        description: "Failed to add reaction",
        variant: "destructive",
      });
    }
  };

  const deleteMessage = async (messageId: string) => {
    try {
      await groupChatService.deleteGroupMessage(messageId);
    } catch (error) {
      console.error("Error deleting message:", error);
      toast({
        title: "Error",
        description: "Failed to delete message",
        variant: "destructive",
      });
    }
  };

  const updateTypingStatus = async (isTyping: boolean) => {
    if (!groupChatId) return;

    try {
      await groupChatService.updateGroupTypingStatus(groupChatId, isTyping);
    } catch (error) {
      console.error("Error updating typing status:", error);
    }
  };

  return {
    groupChatId,
    messages,
    reactions,
    typingUsers,
    loading,
    sending,
    connectionStatus,
    sendMessage,
    handleReaction,
    deleteMessage,
    updateTypingStatus
  };
};
